import React, { useEffect, useState } from 'react'
import {
  Tabs,
  Tab,
  Box,
  Card,
  CardContent,
  Typography,
  Chip
} from '@mui/material'
import { hashHistory } from 'react-router'
import Grid from '@material-ui/core/Grid'
import ButtonInc from '../../../../../shared-ui/ButtonInc'
import TablePaginationInc from '../../../../../shared-ui/TablePaginationInc'
import {
  formatDateLocale,
  formatPepMatchScore
} from '../../../../../lib/utils.es6'
import { useProductCurrencies } from '../../../../../hooks/useProductCurrency'
import { getIPRSFlaggedPolicies } from '../../../../../lib/requests.es6'

var DEFAULT_SIZE = 10

const DisplayIPRSFlaggedPolicies = () => {
  var [awaitingApprovalPolicies, setAwaitingApprovalPolicies] = useState([])
  var [rejectedPolicies, setRejectedPolicies] = useState([])

  var [awaitingApprovalTotal, setAwaitingApprovalTotal] = useState(0)
  var [rejectedTotal, setRejectedTotal] = useState(0)

  var [loading, setLoading] = useState(false)
  var [tab, setTab] = useState(0)

  // Per-tab pagination (0-based page for the UI component)
  var [awaitingPage, setAwaitingPage] = useState(0)
  var [awaitingSize, setAwaitingSize] = useState(DEFAULT_SIZE)
  var [rejectedPage, setRejectedPage] = useState(0)
  var [rejectedSize, setRejectedSize] = useState(DEFAULT_SIZE)

  // Resolve currency from each policy's product (falling back to partner
  // country currency) so premiums show the product currency, not the partner
  // country's — these differ for OMK (products priced in KES).
  const resolveCurrency = useProductCurrencies(
    awaitingApprovalPolicies.concat(rejectedPolicies).map(function(policy) {
      return policy && policy.product_code
    })
  )

  useEffect(function() {
    if (tab === 0) {
      fetchAwaitingApprovalPolicies(awaitingPage, awaitingSize)
    } else {
      fetchRejectedPolicies(rejectedPage, rejectedSize)
    }
    // eslint-disable-next-line
  }, [tab, awaitingPage, awaitingSize, rejectedPage, rejectedSize])

  var fetchAwaitingApprovalPolicies = function(page, size) {
    setLoading(true)
    getIPRSFlaggedPolicies({
      status: 'Un_Confirmed',
      iprs_flagged: 'false',
      page: page + 1,
      size: size
    }).then(function(response) {
      var data =
        response && response.data ? response.data : { data: [], total: 0 }
      setAwaitingApprovalPolicies(data.data || [])
      setAwaitingApprovalTotal(data.total || 0)
    }).catch(function() {
      setAwaitingApprovalPolicies([])
      setAwaitingApprovalTotal(0)
    }).finally(function() {
      setLoading(false)
    })
  }

  var fetchRejectedPolicies = function(page, size) {
    setLoading(true)
    getIPRSFlaggedPolicies({
      status: 'Rejected',
      iprs_flagged: 'false',
      page: page + 1,
      size: size
    }).then(function(response) {
      var data =
        response && response.data ? response.data : { data: [], total: 0 }
      setRejectedPolicies(data.data || [])
      setRejectedTotal(data.total || 0)
    }).catch(function() {
      setRejectedPolicies([])
      setRejectedTotal(0)
    }).finally(function() {
      setLoading(false)
    })
  }

  var getCurrentTabData = function() {
    if (tab === 1) {
      return {
        policies: rejectedPolicies,
        total: rejectedTotal,
        page: rejectedPage,
        size: rejectedSize
      }
    }
    return {
      policies: awaitingApprovalPolicies,
      total: awaitingApprovalTotal,
      page: awaitingPage,
      size: awaitingSize
    }
  }

  var handleTabChange = function(_, v) {
    setTab(v)
    if (v === 0) setAwaitingPage(0)
    else setRejectedPage(0)
  }

  var handlePaginationChange = function(newPage, newSize) {
    if (tab === 0) {
      setAwaitingPage(newPage)
      setAwaitingSize(newSize)
    } else {
      setRejectedPage(newPage)
      setRejectedSize(newSize)
    }
  }

  var tabData = getCurrentTabData()

  // Get principal dependent name
  const getPrincipalName = policy => {
    // Try to get from additional_details first
    if (
      policy.additional_details &&
      policy.additional_details.principal_full_names
    ) {
      return policy.additional_details.principal_full_names
    }

    // Try to get from mainMember
    if (
      policy.additional_details &&
      policy.additional_details.mainMember &&
      policy.additional_details.mainMember.fullName
    ) {
      return policy.additional_details.mainMember.fullName
    }

    // Try to get from quotation dependants - find the Principal entry
    // (it is not guaranteed to be the first schedule entry)
    if (
      policy.quotation &&
      policy.quotation.policy_schedule_entries &&
      policy.quotation.policy_schedule_entries.length > 0
    ) {
      const principalEntry = policy.quotation.policy_schedule_entries.find(
        entry => entry.dependant && entry.dependant.relationship === 'Principal'
      )
      if (principalEntry && principalEntry.dependant) {
        const firstName = principalEntry.dependant.first_name || ''
        const lastName = principalEntry.dependant.last_name || ''
        const fullName = `${firstName} ${lastName}`.trim()
        if (fullName) {
          return fullName
        }
      }
    }

    // Fall back to the principal name on additional_details
    if (
      policy.additional_details &&
      (policy.additional_details.first_name ||
        policy.additional_details.last_name)
    ) {
      const firstName = policy.additional_details.first_name || ''
      const lastName = policy.additional_details.last_name || ''
      const fullName = `${firstName} ${lastName}`.trim()
      if (fullName) {
        return fullName
      }
    }

    return 'N/A'
  }

  var renderPolicyCard = function(policy, index) {
    const principalName = getPrincipalName(policy)
    const premium =
      policy.quotation && policy.quotation.total_premium_amount_in_cents
        ? `${resolveCurrency(policy.product_code)} ${policy.quotation
            .total_premium_amount_in_cents / 100}`
        : 'N/A'

    return (
      <Card
        key={policy.guid || index}
        sx={{
          mb: 2,
          boxShadow: 3,
          '&:hover': {
            boxShadow: 6,
            transform: 'translateY(-2px)',
            transition: 'all 0.3s ease'
          }
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 2
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: '#2d3748', mb: 1 }}
              >
                {policy.policy_number || 'N/A'}
              </Typography>
              <Typography variant="body1" sx={{ color: '#4a5568', mb: 1 }}>
                <strong>Principal:</strong> {principalName}
              </Typography>
              <Typography variant="body2" sx={{ color: '#718096' }}>
                <strong>Beneficiary:</strong> {policy.beneficiary_name || 'N/A'}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: 1
              }}
            >
              {/* Tab-specific IPRS status chips */}
              {tab === 0 &&
                policy.additional_details &&
                policy.additional_details.iprs_flagged && (
                  <Chip
                    label="IPRS FLAGGED"
                    color="error"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                )}

              {tab === 1 && (
                <Chip
                  label={`IPRS ${
                    policy.additional_details &&
                    policy.additional_details.iprs_action
                      ? policy.additional_details.iprs_action.toUpperCase()
                      : 'REJECTED'
                  }`}
                  color="error"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              )}

              <Chip
                label={policy.active ? 'Active' : 'Inactive'}
                color={policy.active ? 'success' : 'default'}
                variant="outlined"
                size="small"
              />
            </Box>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ color: '#718096' }}>
                Status
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {policy.status || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ color: '#718096' }}>
                Premium
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {premium}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ color: '#718096' }}>
                Created
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {formatDateLocale(policy.created_at) || 'N/A'}
              </Typography>
            </Grid>

            {/* IPRS position, when available */}
            {policy.additional_details &&
              policy.additional_details.iprs_position && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" sx={{ color: '#718096' }}>
                    IPRS Position
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {policy.additional_details.iprs_position}
                  </Typography>
                </Grid>
              )}

            {/* IPRS match score, when available */}
            {policy.additional_details &&
              formatPepMatchScore(policy.additional_details.match_score) && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" sx={{ color: '#718096' }}>
                    IPRS Match Score
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatPepMatchScore(policy.additional_details.match_score)}
                  </Typography>
                </Grid>
              )}

            {/* Tab-specific additional information */}
            {tab === 1 &&
              policy.additional_details &&
              policy.additional_details.iprs_reason && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" sx={{ color: '#718096' }}>
                    Reason
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 500, fontSize: '0.85rem' }}
                  >
                    {policy.additional_details.iprs_reason.length > 50
                      ? `${policy.additional_details.iprs_reason.substring(
                          0,
                          50
                        )}...`
                      : policy.additional_details.iprs_reason}
                  </Typography>
                </Grid>
              )}
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
            <ButtonInc
              onClick={() =>
                hashHistory.push({
                  pathname: `admin/policy-processing/${policy.policy_number}`,
                  query: { from: 'iprs_dashboard' }
                })
              }
              variant="contained"
              sx={{
                minWidth: 120,
                fontWeight: 600,
                textTransform: 'none'
              }}
            >
              View Policy
            </ButtonInc>
          </Box>
        </CardContent>
      </Card>
    )
  }

  return (
    <React.Fragment>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={handleTabChange}>
          <Tab label="Awaiting Review" />
          <Tab label="Rejected" />
        </Tabs>
      </Box>
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" sx={{ color: '#718096' }}>
            Loading policies...
          </Typography>
        </Box>
      ) : tabData.policies.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" sx={{ color: '#718096' }}>
            No policies found
          </Typography>
        </Box>
      ) : (
        <Box>
          {tabData.policies.map(renderPolicyCard)}
          <TablePaginationInc
            page={tabData.page}
            size={tabData.size}
            total={tabData.total}
            onChange={handlePaginationChange}
          />
        </Box>
      )}
    </React.Fragment>
  )
}

export default DisplayIPRSFlaggedPolicies
